import { Component, Suspense, lazy, useEffect, useState, type ErrorInfo, type ReactNode } from "react"
import { useLocation, useNavigate, Navigate } from "react-router-dom"
import { useAuth, type UserRole } from "../components/AuthProvider"
import { pathnameToPage, ROUTES } from "../lib/routes"
import { recordPageVisit } from "../lib/useRecentPages"
import { supabase } from "../lib/supabase"
import NotFoundPage from "../pages/NotFoundPage"
import LoginPage from "../pages/LoginPage"
import ResetPasswordPage from "../pages/ResetPasswordPage"
import UnauthorizedPage from "../pages/UnauthorizedPage"

/** Visits to /logout: sign out and redirect to login. */
function LogoutRedirect() {
  const signOut = useAuth().signOut
  const navigate = useNavigate()
  useEffect(() => {
    signOut().then(() => navigate(ROUTES.login, { replace: true }))
  }, [signOut, navigate])
  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-950">
      <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-white/50 animate-spin" />
    </div>
  )
}

const DashboardPageV2 = lazy(() => import("../pages/DashboardPageV2"))
const AdminPredictionsPage = lazy(() => import("../pages/AdminPredictionsPage"))
const ServiceHistoryPage = lazy(() => import("../pages/ServiceHistoryPage"))
const AdminUsersPage = lazy(() => import("../pages/AdminUsersPage"))
const AdminLeadsPage = lazy(() => import("../pages/AdminLeadsPage"))
const SettingsPage = lazy(() => import("../pages/SettingsPage"))
const DocsPage = lazy(() => import("../pages/DocsPage"))
const PropertiesPage = lazy(() => import("../pages/PropertiesPage"))
const OnboardingPage = lazy(() => import("../pages/OnboardingPage"))
const ApplyPage = lazy(() => import("../pages/ApplyPage"))
const ApplyEnterprisePage = lazy(() => import("../pages/ApplyEnterprisePage"))
const TopicaPage = lazy(() => import("../pages/TopicaPage"))
const ManifestPage = lazy(() => import("../pages/ManifestPage"))
const CreatorDocsPage = lazy(() => import("../pages/CreatorDocsPage"))
const PublicTracePage = lazy(() => import("../pages/PublicTracePage"))
const SchedulePage = lazy(() => import("../pages/SchedulePage"))
const ProtocolsPage = lazy(() => import("../pages/ProtocolsPage"))
const BillingPage = lazy(() => import("../pages/BillingPage"))
const CheckoutSuccessPage = lazy(() => import("../pages/CheckoutSuccessPage"))
const CreatorFunnelPage = lazy(() => import("../pages/CreatorFunnelPage"))
const ToolsFeaturesPage = lazy(() => import("../pages/ToolsFeaturesPage"))
const CapacitorPage = lazy(() => import("../pages/CapacitorPage"))
const WelcomePage = lazy(() => import("../pages/WelcomePage"))
const PreviewGlitchPage = lazy(() => import("../pages/preview/PreviewGlitchPage"))

function PageSpinner() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-white/50 animate-spin" />
    </div>
  )
}

class LazyErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error("Lazy load failed:", error, info) }
  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-stone-600 dark:text-stone-400 text-sm">Failed to load this page. Check your connection and try again.</p>
        <button
          onClick={() => { this.setState({ hasError: false }); window.location.reload() }}
          className="px-4 py-2 rounded-lg bg-stone-200 dark:bg-stone-700 text-stone-800 dark:text-stone-200 text-sm font-medium hover:bg-stone-300 dark:hover:bg-stone-600"
        >
          Reload
        </button>
      </div>
    )
  }
}

/**
 * Single router: no /v2 prefix. All authenticated routes use pathname (/, /schedule, /help, /admin/*, etc.).
 */
export function Router() {
  const location = useLocation()
  const navigate = useNavigate()
  const { page: currentPage, recordId } = pathnameToPage(location.pathname)
  const { user, role, companyId, isDemo, loading } = useAuth()
  const [checkingNewTenant, setCheckingNewTenant] = useState(true)
  const [trialExpired, setTrialExpired] = useState(false)

  // Redirect new tenants (admin with companyId, zero clients for this scope) to onboarding
  useEffect(() => {
    if (role !== "admin" || !companyId || isDemo || currentPage === "onboarding") {
      setCheckingNewTenant(false)
      return
    }
    let cancelled = false
    supabase
      .from("clients")
      .select("id", { count: "exact", head: true })
      .eq("company_id", companyId)
      .eq("is_demo", isDemo)
      .then(
        ({ count }) => {
          if (cancelled) return
          if (count === 0) navigate(ROUTES.onboarding, { replace: true })
          setCheckingNewTenant(false)
        },
        () => setCheckingNewTenant(false)
      )
    return () => { cancelled = true }
  }, [role, companyId, isDemo, currentPage, navigate])

  // Trial expiry guard — redirect expired+unsubscribed admins to /billing
  useEffect(() => {
    if (role !== "admin" || !companyId || currentPage === "billing" || currentPage === "billing/success") {
      setTrialExpired(false)
      return
    }
    let cancelled = false
    supabase
      .from("companies")
      .select("trial_ends_at, stripe_subscription_id")
      .eq("id", companyId)
      .single()
      .then(({ data }) => {
        if (cancelled || !data) return
        const expired = !!data.trial_ends_at && new Date(data.trial_ends_at) < new Date()
        const subscribed = !!data.stripe_subscription_id
        if (expired && !subscribed) {
          setTrialExpired(true)
          navigate(ROUTES.billing, { replace: true })
        }
      })
    return () => { cancelled = true }
  }, [role, companyId, currentPage, navigate])

  useEffect(() => {
    if (user && !loading) recordPageVisit(location.pathname + location.search)
  }, [location.pathname, location.search, user, loading])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-white/50 animate-spin" />
      </div>
    )
  }

  // /preview/* — public, chromeless, embeddable specimen surfaces (no auth required)
  // Add new preview pages here as lazy imports + a new currentPage check.
  if (location.pathname.startsWith("/preview/")) {
    if (currentPage === "preview/glitch") {
      return <LazyErrorBoundary><Suspense fallback={<PageSpinner />}><PreviewGlitchPage /></Suspense></LazyErrorBoundary>
    }
    // Unknown /preview/* path → render nothing (avoids infinite 404 loop)
    return null
  }

  // Legacy not-found alias: keep one canonical 404 URL.
  if (location.pathname === "/not-found") {
    return <Navigate to={ROUTES.notFound} replace />
  }

  // /welcome — public landing page for Stripe business verification (no auth required)
  if (currentPage === "welcome") {
    return <LazyErrorBoundary><Suspense fallback={<PageSpinner />}><WelcomePage /></Suspense></LazyErrorBoundary>
  }

  // /billing/success is public — Stripe redirects here after checkout (no auth required)
  if (currentPage === "billing/success") {
    return <LazyErrorBoundary><Suspense fallback={<PageSpinner />}><CheckoutSuccessPage /></Suspense></LazyErrorBoundary>
  }

  // /trace and /trace/:companySlug — public status pages
  if (location.pathname === "/trace" || location.pathname.startsWith("/trace/")) {
    return <LazyErrorBoundary><Suspense fallback={<PageSpinner />}><PublicTracePage /></Suspense></LazyErrorBoundary>
  }

  if (!user && !isDemo && currentPage !== "login" && currentPage !== "logout" && currentPage !== "apply" && currentPage !== "apply/enterprise" && currentPage !== "reset-password" && currentPage !== "404") {
    return <Navigate to="/login" replace />
  }

  // Onboarding is only for invited tenants: must be signed in and have a company. No one else can open /onboarding (blocks demo visitors and spam).
  if (currentPage === "onboarding") {
    if (!user) return <Navigate to={ROUTES.apply} replace />
    if (!companyId) return <Navigate to={ROUTES.home} replace />
  }

  if (currentPage === "reset-password") {
    return <ResetPasswordPage />
  }

  if (currentPage === "404") {
    return <NotFoundPage />
  }

  if (currentPage === "apply") {
    if (user) return <Navigate to={ROUTES.home} replace />
    return <LazyErrorBoundary><Suspense fallback={<PageSpinner />}><ApplyPage /></Suspense></LazyErrorBoundary>
  }

  if (currentPage === "apply/enterprise") {
    if (user) return <Navigate to={ROUTES.home} replace />
    return <LazyErrorBoundary><Suspense fallback={<PageSpinner />}><ApplyEnterprisePage /></Suspense></LazyErrorBoundary>
  }

  if (currentPage === "login") {
    // Let users log in on demo.luster.company so they get demo data (is_demo = true).
    if (!user) return <LoginPage />
    return <Navigate to={ROUTES.home} replace />
  }

  if (currentPage === "logout") {
    return <LogoutRedirect />
  }

  if (user && role === null) {
    return <UnauthorizedPage />
  }

  if (checkingNewTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-white/50 animate-spin" />
      </div>
    )
  }

  const r: UserRole = role
  const isAdmin = r === "admin" || r === "creator"
  const isOperator = r === "operator"

  if (isOperator && typeof currentPage === "string" && (currentPage.startsWith("admin/") || currentPage.startsWith("creator/"))) {
    return <NotFoundPage />
  }

  let page: ReactNode

  switch (currentPage) {
    case undefined:
    case null:
    case "":
    case "home":
    case "admin":
      page = <DashboardPageV2 />
      break

    case "schedule":
      page = <SchedulePage />
      break

    case "activities":
      page = <ServiceHistoryPage />
      break

    case "properties":
      page = <PropertiesPage />
      break

    case "onboarding":
      page = <OnboardingPage />
      break

    case "admin/users":
      if (!isAdmin) return <NotFoundPage />
      page = <AdminUsersPage />
      break

    // Note: admin/admins removed — unified into /admin/users with tabs (IF-422)
    case "admin/admins":
      return isAdmin ? <Navigate to="/admin/users?tab=admins" replace /> : <NotFoundPage />

    case "admin/properties":
      if (!isAdmin) return <NotFoundPage />
      if (recordId) return <Navigate to={`/admin/properties?edit=${recordId}`} replace />
      page = <PropertiesPage />
      break
    case "admin/time":
      if (!isAdmin) return <NotFoundPage />
      return <Navigate to={ROUTES.adminTime} replace />

    case "admin/protocols":
      if (!isAdmin) return <NotFoundPage />
      page = <ProtocolsPage />
      break

    case "admin/predictions":
      if (!isAdmin) return <NotFoundPage />
      page = <AdminPredictionsPage />
      break

    case "creator/leads":
      if (r !== "creator") return <NotFoundPage />
      page = <AdminLeadsPage />
      break

    case "creator/manifest":
      if (r !== "creator") return <NotFoundPage />
      page = <ManifestPage />
      break

    case "creator/topica":
      if (r !== "creator") return <NotFoundPage />
      page = <TopicaPage />
      break

    case "creator/trace":
      return <Navigate to={ROUTES.traceFeed} replace />

    case "creator/docs":
      if (r !== "creator") return <NotFoundPage />
      page = <CreatorDocsPage />
      break

    case "creator/funnel":
      if (r !== "creator") return <NotFoundPage />
      page = <CreatorFunnelPage />
      break

    case "creator/tools-features":
      if (r !== "creator") return <NotFoundPage />
      page = <ToolsFeaturesPage />
      break

    case "creator/capacitor":
      if (r !== "creator") return <NotFoundPage />
      page = <CapacitorPage />
      break

    case "my-trace":
      if (r === "creator") return <Navigate to={ROUTES.traceFeed} replace />
      page = <PublicTracePage />
      break

    case "settings":
      page = <SettingsPage />
      break

    case "billing":
      if (!isAdmin || isDemo) return <NotFoundPage />
      page = <BillingPage />
      break

    default:
      if (typeof currentPage === "string" && (currentPage === "docs" || currentPage.startsWith("docs/"))) {
        page = <DocsPage />
      } else {
        return <NotFoundPage />
      }
      break
  }

  return <LazyErrorBoundary><Suspense fallback={<PageSpinner />}>{page}</Suspense></LazyErrorBoundary>
}
